import React, { JSX, useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';

import OrderSearchForm, { MultiFilterState } from '~/components/Common/Forms/OrderSearchForm';
import Spinner from '~/components/Common/Spinner';
import { withTenant } from '~/components/hoc';

import { ITEM_TYPE_CAR_HIRE } from '~/consts/order';
import { ROLE_ICS_STORE_TEAM } from '~/consts/roles';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';

import { getPurchases } from '~/services/OrdersService';

import { addQuery, parseSearchString, removeQuery } from '~/utils/url';

import CarHirePurchaseList from './CarHirePurchaseList';

const DEFAULT_SIZE_PER_PAGE = 10;

type Props = {
  tenant: App.Tenant;
};

function CarHirePurchasePage({ tenant }: Props): JSX.Element {
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { push: setQueryString } = useAppNavigation();
  const { user } = useCurrentUser();

  const [filter, setFilter] = useState({
    page: null,
    field: null,
    value: null,
    endDate: null,
    startDate: null,
    customerId: null,
  });
  const [additionalSearchParams, setAdditionalSearchParams] = useState<MultiFilterState>(null);
  // bumped on every submit so repeating an identical search still refetches
  const [searchNonce, setSearchNonce] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState([]);
  const [total, setTotal] = useState(0);

  const onPageChange = async (page: number): Promise<void> => {
    setQueryString(addQuery(location, { page }));
  };

  const handleSearchString = ({ customer_id, page }): void => {
    setFilter((prev) => ({
      ...prev,
      customerId: customer_id as string,
      page: parseInt(page as string) || 1,
    }));
  };

  const searchQuery = async (field, value): Promise<void> => {
    setFilter((prev) => ({ ...prev, field, value }));
    setSearchNonce((nonce) => nonce + 1);

    setQueryString(removeQuery(location, 'customer_id', 'page'));
  };

  const fetchPurchaseList = useCallback(async (): Promise<void> => {
    if (user?.roles.includes(ROLE_ICS_STORE_TEAM)) {
      // we don't want these users to be able to see all results so
      // if there are no filters selected, just return empty
      if (!filter.field || !filter.value) {
        setResult([]);
        setTotal(0);
        setLoading(false);
        return;
      }
    }

    try {
      setLoading(true);
      const { result, total } = await getPurchases({
        page: filter.page,
        per_page: DEFAULT_SIZE_PER_PAGE,
        brand: tenant.brand,
        customer_id: filter.customerId,
        filterBy: filter.field,
        filterValue: filter.value,
        additionalFilter: additionalSearchParams,
        item_type: ITEM_TYPE_CAR_HIRE,
      });

      setTotal(total);
      setResult(result);
    } catch (e) {
      enqueueSnackbar('Failed to load data', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [
    user?.roles,
    filter.field,
    filter.value,
    filter.page,
    filter.customerId,
    tenant.brand,
    additionalSearchParams,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    const { customer_id, page } = parseSearchString(location.search);
    handleSearchString({ customer_id, page });
  }, [location.search]);

  useEffect(() => {
    if (filter.page && user) {
      fetchPurchaseList();
    }
  }, [filter, fetchPurchaseList, user, searchNonce]);

  return (
    <>
      <OrderSearchForm
        e2eSuffix="-car-hire"
        searchQuery={searchQuery}
        bookingIdType="booking_numbers"
        setAdditionalParams={setAdditionalSearchParams}
        bookingNumberPlaceholder="Enter Car Hire Booking Number"
        showCarHireFilter
      />

      <>
        {loading && <Spinner size={48} />}

        {!loading && result && (
          <CarHirePurchaseList
            page={filter.page}
            total={total}
            orders={result}
            sizePerPage={DEFAULT_SIZE_PER_PAGE}
            onPageChange={onPageChange}
            rerender={fetchPurchaseList}
          />
        )}
      </>
    </>
  );
}

export default withTenant(CarHirePurchasePage);
